import CountdownToEndOfDay from '@components/CountDownToEOD'
import { DewiCard } from '@containers/auction'
import useAuctionManager from '@hooks/useAuctionManager'
import { useMetaplexMetadata } from '@hooks/useMetaplexMetadata'
import { ellipsizeAddress } from '@utils/solanaUtils'
import { isPast } from 'date-fns'
import Image from 'next/image'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import JazzIcon from '@components/JazzIcon'

type AuctionBodyProps = {
  currentDewiCat: DewiCard
  currentBid: number | undefined
}

const AuctionBody = ({ currentDewiCat, currentBid }: AuctionBodyProps) => {
  const { t } = useTranslation()
  const { currentListing, highestBidder, currentCat, allCats } =
    useAuctionManager()
  const { image } = useMetaplexMetadata(currentListing?.tokenMint)

  const listingEnded = useMemo(() => {
    const auctionEndDate = new Date(currentListing?.endAt.toNumber() * 1000)
    return isPast(auctionEndDate)
  }, [currentListing])

  const priceToDisplay = useMemo(() => {
    if (listingEnded) {
      return currentDewiCat?.soldPrice?.toLocaleString()
    }

    if (currentBid) {
      return currentBid?.toLocaleString()
    }

    if (currentListing) {
      return currentListing?.startingPrice?.toLocaleString()
    }

    return undefined
  }, [listingEnded, currentDewiCat, currentBid, currentListing])

  const ListingEndingTextToDisplay = useCallback(() => {
    if (!currentListing) {
      return (
        <h4 className="w-32 h-10 rounded bg-dark-7 animate-pulse mt-2 text-size-4 font-semibold" />
      )
    }

    if (!listingEnded) {
      return <CountdownToEndOfDay />
    }

    return (
      <div className="flex items-center gap-2">
        <JazzIcon
          publicAddress={highestBidder?.bidder?.toBase58()}
          diameter={30}
        />
        <h4 className="xs:text-size-3 lg:text-size-5">
          {ellipsizeAddress(highestBidder?.bidder?.toBase58(), {
            numChars: 4,
          })}
        </h4>
      </div>
    )
  }, [listingEnded, currentListing, highestBidder])

  const openExplorer = useCallback(() => {
    // open explorer solana token mint in new tab
    window.open(
      `https://explorer.solana.com/address/${currentListing.tokenMint.toBase58()}`,
      '_blank',
    )
  }, [currentListing])

  const catIndex = useMemo(() => {
    return allCats?.findIndex((cat) => cat.id === currentCat?.id)
  }, [allCats, currentCat])

  if (currentCat && !currentListing && !currentBid && catIndex > 68) {
    return (
      <>
        <h2 className="text-gray-3 text-size-4 xs:text-center md:text-left">
          {t('auction.giftedLegendaries.title')}
        </h2>
        <h4 className="text-gray-6 xs:text-center md:text-left">
          {t('auction.giftedLegendaries.subtitle1')}
        </h4>
        <h4 className="text-gray-6 xs:text-center md:text-left">
          {t('auction.giftedLegendaries.subtitle2')}
        </h4>
        <h4 className="text-gray-6 xs:text-center md:text-left">
          {t('auction.giftedLegendaries.subtitle3')}
          <a href="/dewihall" className="text-blue-5">
            {t('auction.giftedLegendaries.dewiHall')}
          </a>
        </h4>
      </>
    )
  }

  // if current DeWiCat and  no current bid is available we need to show text that this DeWiCat was minted for allowlist members at the DeWiCat Genesis days
  if (currentCat && !currentListing && !currentBid) {
    return (
      <>
        <h2 className="text-gray-3 text-size-4 xs:text-center md:text-left">
          {t('auction.originalSixtyNine.title')}
        </h2>
        <h4 className="text-gray-6 xs:text-center md:text-left">
          {t('auction.originalSixtyNine.subtitle1')}
        </h4>
        <h4 className="text-gray-6 xs:text-center md:text-left">
          {t('auction.originalSixtyNine.subtitle2')}
        </h4>
      </>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 font-space font-bold pb-0 md:pb-0">
      <div className="grid w-full grid-cols-1 md:grid-cols-2 place-items-center md:place-items-unset justify-center border-dark-6 border-b-[1px]">
        <div className="flex flex-row border-dark-6 xs:border-r-0 lg:border-r-[1px] xs:border-b-[1px] lg:border-b-0 xs:w-full lg:w-auto xs:justify-center lg:justify-start">
          <div className="flex flex-col gap-2 items-center md:items-start p-4">
            <div className="flex gap-2 items-center">
              <h4 className="text-gray-6 text-size-1 font-medium">
                {listingEnded ? t('auction.bid.done') : t('auction.bid.active')}
              </h4>
            </div>
            <div className="flex gap-2 items-center">
              <p className="text-size-4 font-semibold">
                {priceToDisplay ? (
                  priceToDisplay
                ) : (
                  <h2 className="w-32 h-10 rounded bg-dark-7 animate-pulse mt-2" />
                )}
              </p>
              {priceToDisplay && (
                <Image
                  src={image}
                  alt="auction-token"
                  width={26}
                  height={26}
                  data-loaded="false"
                  onLoad={(event) => {
                    event.currentTarget.setAttribute('data-loaded', 'true')
                    event.currentTarget.style.opacity = '1'
                  }}
                  onClick={openExplorer}
                  className={`data-[loaded=false]:animate-pulse data-[loaded=false]:bg-dark-7 rounded-full opacity-0 cursor-pointer`}
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 items-center md:items-start p-4">
          <h4 className="text-gray-6 text-size-1 font-medium">
            {listingEnded ? t('auction.ownerTitle') : t('auction.timeTitle')}
          </h4>
          <ListingEndingTextToDisplay />
        </div>
      </div>
    </div>
  )
}

export default AuctionBody
